import { useCallback, useEffect, useState, type ReactNode } from 'react';
import { RefreshCw, Wand2 } from 'lucide-react';
import {
  DEFAULT_OPTIONS,
  DEFAULT_PASSPHRASE_OPTIONS,
  PASSPHRASE_MAX_WORDS,
  PASSPHRASE_MIN_WORDS,
  PASSWORD_MAX,
  PASSWORD_MIN,
  estimateEntropyBits,
  estimateMemorableEntropyBits,
  estimatePassphraseEntropyBits,
  generateMemorable,
  generatePassphrase,
  generatePassword,
  strengthLabel,
  type PassphraseOptions,
  type PasswordOptions,
} from '@/lib/passwordGen';
import { cn } from '@/lib/cn';
import { CopyButton } from '@/pages/OnPass/CopyButton';

// 'words'  = Memorable       — a short pattern (Brave!23)
// 'phrase' = Extra memorable — a multi-word passphrase (brave-otter-lamp-cove-27)
// 'chars'  = Super strong    — a long, configurable random string
type Mode = 'words' | 'phrase' | 'chars';

/**
 * Client-side password generator (PR-25 + memorable / extra-memorable / super
 * strong modes). Generates entirely in the browser via the Web Crypto API;
 * nothing is sent anywhere until the user clicks "Use this", which hands the
 * value to the caller (e.g. the OnPass create form).
 *
 * Three modes:
 *   • Memorable      — the default; a short, easy-to-read pattern (Brave!23).
 *   • Extra memorable — a longer multi-word passphrase, easy to read / dictate.
 *   • Super strong    — a long, configurable random string (defaults to 40 chars).
 */
export function PasswordGenerator({ onUse }: { onUse: (value: string) => void }) {
  const [mode, setMode] = useState<Mode>('words');
  const [options, setOptions] = useState<PasswordOptions>(DEFAULT_OPTIONS);
  const [phrase, setPhrase] = useState<PassphraseOptions>(DEFAULT_PASSPHRASE_OPTIONS);
  const [value, setValue] = useState('');
  const [error, setError] = useState<string | null>(null);

  const regenerate = useCallback(() => {
    try {
      setValue(
        mode === 'words'
          ? generateMemorable()
          : mode === 'phrase'
            ? generatePassphrase(phrase)
            : generatePassword(options),
      );
      setError(null);
    } catch (e) {
      setValue('');
      setError(e instanceof Error ? e.message : 'Could not generate a password.');
    }
  }, [mode, options, phrase]);

  // Generate on mount and whenever the mode or its options change.
  useEffect(() => { regenerate(); }, [regenerate]);

  const setOpt = <K extends keyof PasswordOptions>(key: K, v: PasswordOptions[K]) =>
    setOptions((o) => ({ ...o, [key]: v }));
  const setPhraseOpt = <K extends keyof PassphraseOptions>(key: K, v: PassphraseOptions[K]) =>
    setPhrase((o) => ({ ...o, [key]: v }));

  const bits =
    mode === 'words'
      ? estimateMemorableEntropyBits()
      : mode === 'phrase'
        ? estimatePassphraseEntropyBits(phrase)
        : estimateEntropyBits(options);

  return (
    <div className="rounded-md border border-border bg-bg p-3">
      <div className="mb-3 inline-flex flex-wrap gap-0.5 rounded border border-border bg-white p-0.5 text-xs font-semibold">
        <ModeButton active={mode === 'words'} onClick={() => setMode('words')}>Memorable</ModeButton>
        <ModeButton active={mode === 'phrase'} onClick={() => setMode('phrase')}>Extra memorable</ModeButton>
        <ModeButton active={mode === 'chars'} onClick={() => setMode('chars')}>Super strong</ModeButton>
      </div>

      <div className="flex items-center gap-2">
        <input
          readOnly
          value={value}
          aria-label="Generated password"
          className="min-w-0 flex-1 rounded border border-border bg-white px-2 py-1.5 font-mono text-sm"
        />
        <button
          type="button"
          onClick={regenerate}
          aria-label="Regenerate"
          className="inline-flex items-center gap-1.5 rounded border border-border bg-white px-2.5 py-1.5 text-sm font-medium text-darkGrey hover:bg-bg"
        >
          <RefreshCw className="h-4 w-4" aria-hidden />
        </button>
        <CopyButton value={value} />
      </div>

      {error && <p role="alert" className="mt-2 text-sm text-red">{error}</p>}

      <div className="mt-2 flex items-center justify-between text-xs text-darkGrey">
        <span>≈ {bits} bits — {strengthLabel(bits)}</span>
        <button
          type="button"
          onClick={() => value && onUse(value)}
          disabled={!value}
          className="inline-flex items-center gap-1.5 rounded bg-orange px-3 py-1 text-sm font-semibold text-white hover:opacity-90 disabled:opacity-50"
        >
          <Wand2 className="h-3.5 w-3.5" aria-hidden />
          Use this
        </button>
      </div>

      {mode === 'chars' && (
        <div className="mt-3 space-y-2">
          <label className="flex items-center gap-2 text-xs font-semibold text-darkGrey">
            Length: {options.length}
            <input
              type="range"
              min={PASSWORD_MIN}
              max={PASSWORD_MAX}
              value={options.length}
              onChange={(e) => setOpt('length', Number(e.target.value))}
              aria-label="Length"
              className="flex-1"
            />
          </label>
          <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-darkGrey">
            <Toggle label="A-Z" checked={options.uppercase} onChange={(v) => setOpt('uppercase', v)} />
            <Toggle label="a-z" checked={options.lowercase} onChange={(v) => setOpt('lowercase', v)} />
            <Toggle label="0-9" checked={options.digits} onChange={(v) => setOpt('digits', v)} />
            <Toggle label="Symbols" checked={options.symbols} onChange={(v) => setOpt('symbols', v)} />
            <Toggle label="Exclude ambiguous" checked={options.excludeAmbiguous} onChange={(v) => setOpt('excludeAmbiguous', v)} />
          </div>
        </div>
      )}

      {mode === 'phrase' && (
        <div className="mt-3 space-y-2">
          <label className="flex items-center gap-2 text-xs font-semibold text-darkGrey">
            Words: {phrase.words}
            <input
              type="range"
              min={PASSPHRASE_MIN_WORDS}
              max={PASSPHRASE_MAX_WORDS}
              value={phrase.words}
              onChange={(e) => setPhraseOpt('words', Number(e.target.value))}
              aria-label="Words"
              className="flex-1"
            />
          </label>
          <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-darkGrey">
            <Toggle label="Capitalise" checked={phrase.capitalise} onChange={(v) => setPhraseOpt('capitalise', v)} />
            <Toggle label="Number" checked={phrase.includeNumber} onChange={(v) => setPhraseOpt('includeNumber', v)} />
            <Toggle label="Symbol" checked={phrase.includeSymbol} onChange={(v) => setPhraseOpt('includeSymbol', v)} />
          </div>
          <p className="text-xs text-darkGrey/70">
            A longer multi-word passphrase, easy to read aloud or type (e.g.{' '}
            <span className="font-mono text-darkGrey">brave-otter-lamp-cove-27</span>).
          </p>
        </div>
      )}

      {mode === 'words' && (
        <p className="mt-3 text-xs text-darkGrey/70">
          A short, easy-to-read pattern: one capitalised word, a symbol and two
          numbers (e.g. <span className="font-mono text-darkGrey">Brave!23</span>).
          Use <span className="font-semibold">Extra memorable</span> or{' '}
          <span className="font-semibold">Super strong</span> for a stronger secret.
        </p>
      )}
    </div>
  );
}

function ModeButton({ active, onClick, children }: { active: boolean; onClick: () => void; children: ReactNode }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={cn(
        'rounded px-2.5 py-1 transition-colors',
        active ? 'bg-navy text-white' : 'text-darkGrey hover:bg-bg',
      )}
    >
      {children}
    </button>
  );
}

function Toggle({ label, checked, onChange }: { label: string; checked: boolean; onChange: (v: boolean) => void }) {
  return (
    <label className="inline-flex items-center gap-1.5 font-normal">
      <input type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} />
      {label}
    </label>
  );
}
